import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '../../shared/components/Common';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { formatPrice } from '../../shared/data/common';
import { cachedBusSearch, getSeatMap, reviewBus } from '../../api/bus';
import { cancellationLines } from './data';
import { busQueryString, parseBusQuery, seatsLabel, timeLabel } from './searchModel';
import { BusTimes } from './BusList';
import './bus.css';

const seatState = (seat, selected) => {
  if (!['Seat', 'Sleeper'].includes(seat.kind)) return 'fixture';
  if (!seat.available) return 'booked';
  if (selected) return 'selected';
  return seat.ladiesOnly ? 'ladies' : seat.menOnly ? 'men' : 'free';
};

const seatTitle = (seat) => {
  if (seat.kind === 'Toilet') return 'Washroom';
  if (seat.kind === 'EmergencyExit') return 'Emergency exit';
  const who = seat.ladiesOnly ? ' · ladies only' : seat.menOnly ? ' · men only' : '';
  return `${seat.kind} ${seat.id}${seat.available ? ` · ${formatPrice(seat.total)}${who}` : ' · booked'}`;
};

/**
 * One deck drawn from the front (left, with the driver on the lower deck) to the back. Seats sit on
 * the API's grid: `length` columns along the bus and `width` rows across it.
 */
export function Deck({ deck, selectedIds, onToggle, showDriver }) {
  return (
    <div className="bus-deck">
      <div className="bus-deck-head"><b>{deck.name}</b>{showDriver && <span className="bus-driver" title="Driver"><Icon name="wheel" size={20} /></span>}</div>
      <div className="bus-deck-grid" style={{ gridTemplateColumns: `repeat(${deck.columns}, var(--seat))`, gridTemplateRows: `repeat(${deck.rows}, var(--seat))` }}>
        {deck.seats.map((seat) => {
          const selected = selectedIds.includes(seat.id);
          const state = seatState(seat, selected);
          const style = { gridColumn: `${seat.column + 1} / span ${seat.length}`, gridRow: `${seat.row + 1} / span ${seat.width}` };
          if (state === 'fixture') {
            return <span key={`${seat.id}-${seat.row}-${seat.column}`} className="bus-seat fixture" style={style} title={seatTitle(seat)}>{seat.kind === 'Toilet' ? 'WC' : 'EXIT'}</span>;
          }
          return (
            <button key={seat.id} type="button" style={style} title={seatTitle(seat)} aria-label={seatTitle(seat)} aria-pressed={selected}
              className={`bus-seat ${state} ${seat.kind === 'Sleeper' ? 'sleeper' : ''}`} disabled={state === 'booked'} onClick={() => onToggle(seat)}>
              <span className="bus-seat-id">{seat.id}</span>
              {state !== 'booked' && <span className="bus-seat-fare">{Math.round(seat.total)}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PointList({ title, points, value, onChange, name }) {
  return (
    <div className="bus-points">
      <h4>{title}</h4>
      <div className="bus-point-list" role="radiogroup" aria-label={title}>
        {points.map((p) => (
          <label key={p.id} className={`bus-point ${value === p.id ? 'active' : ''}`}>
            <input type="radio" name={name} checked={value === p.id} onChange={() => onChange(p.id)} />
            <span className="bus-point-time">{p.time ? timeLabel(p.time) : '—'}</span>
            <span><b>{p.name}</b>{(p.landmark || p.address) && <small className="muted">{p.landmark || p.address}</small>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function BusSeats() {
  const q = useQuery();
  const { state } = useLocation();
  const navigate = useNavigate();
  const search = parseBusQuery(q);
  const resultsPath = `/bus/results?${busQueryString(search)}`;
  const busId = q.bus;
  const searchId = state?.searchId || cachedBusSearch(search)?.searchId;
  const [map, setMap] = useState(null);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [selected, setSelected] = useState([]);
  const [boarding, setBoarding] = useState('');
  const [dropping, setDropping] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [reviewError, setReviewError] = useState('');

  useEffect(() => {
    if (!searchId || !busId) return undefined;
    const controller = new AbortController();
    setError('');
    getSeatMap(searchId, busId, controller.signal)
      .then((result) => {
        setMap(result);
        // Keep chosen seats that are still free; pick the only stop when there's just one.
        const free = new Set(result.decks.flatMap((d) => d.seats).filter((s) => s.available).map((s) => s.id));
        setSelected((list) => list.filter((s) => free.has(s.id)));
        if (result.boardingPoints.length === 1) setBoarding(result.boardingPoints[0].id);
        if (result.droppingPoints.length === 1) setDropping(result.droppingPoints[0].id);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return;
        setError(e.status === 409 || e.status === 404 ? 'These search results have expired. Search again to see current buses and seats.' : e.message || 'We could not load the seat map.');
      });
    return () => controller.abort();
  }, [searchId, busId, attempt]);

  if (!busId || !searchId) {
    return (
      <div className="container section empty">
        <span className="empty-icon"><Icon name="bus" size={34} /></span>
        <h2>Choose your bus again</h2>
        <p>Seat maps are live, so we need a fresh search to show the latest seats.</p>
        <Link to={resultsPath} className="btn btn-primary">See buses</Link>
      </div>
    );
  }

  const bus = map?.bus || state?.bus;
  const maxSeats = map?.maxSeatsPerBooking || 6;
  const fare = selected.reduce((sum, s) => sum + s.total, 0);
  const toggle = (seat) => {
    setNotice('');
    setReviewError('');
    if (selected.some((s) => s.id === seat.id)) {
      setSelected(selected.filter((s) => s.id !== seat.id));
    } else if (selected.length >= maxSeats) {
      setNotice(`You can book up to ${maxSeats} seats at a time.`);
    } else {
      setSelected([...selected, seat]);
    }
  };
  const needsDrop = (map?.droppingPoints.length || 0) > 0;
  const ready = map && selected.length > 0 && boarding && (!needsDrop || dropping);

  const continueToBook = async () => {
    setBusy(true);
    setReviewError('');
    try {
      const review = await reviewBus({
        searchId, busId, seatIds: selected.map((s) => s.id), boardingPointId: boarding, droppingPointId: dropping || null,
      });
      navigate('/bus/review', { state: { review, search, backTo: `/bus/seats?${new URLSearchParams(q).toString()}` } });
    } catch (e) {
      if (e.status === 409) setAttempt((n) => n + 1);
      setReviewError(e.message || 'We could not hold these seats. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <FlowBand title={bus ? bus.operator : 'Choose your seats'} step={1} back={{ to: resultsPath, label: 'Back to buses' }} />
      <div className="container details-layout">
        <div className="details-main">
          {bus && (
            <section className="panel bus-head">
              <div>
                <span className="tag">{search.from} → {search.to}</span>
                <h2 className="details-title">{bus.operator}</h2>
                <span className="muted small">{bus.busType}</span>
              </div>
              <BusTimes bus={bus} />
            </section>
          )}

          <section className="panel">
            <h3>Choose seats</h3>
            {error ? (
              <div className="api-message error" role="alert">
                <p>{error}</p>
                <div className="btn-row">
                  {!/expired/i.test(error) && <button type="button" className="btn btn-outline btn-sm" onClick={() => setAttempt(attempt + 1)}>Try again</button>}
                  <Link to={resultsPath} className="btn btn-primary btn-sm">Search again</Link>
                </div>
              </div>
            ) : !map ? (
              <p role="status"><span className="spinner sm" /> Loading live seat map…</p>
            ) : (
              <>
                <div className="bus-legend">
                  <span><i className="bus-seat free" /> Available</span>
                  <span><i className="bus-seat ladies" /> Ladies only</span>
                  {map.decks.some((d) => d.seats.some((s) => s.menOnly)) && <span><i className="bus-seat men" /> Men only</span>}
                  <span><i className="bus-seat selected" /> Selected</span>
                  <span><i className="bus-seat booked" /> Booked</span>
                </div>
                <div className="bus-decks">
                  {map.decks.map((deck, i) => (
                    <Deck key={deck.name} deck={deck} selectedIds={selected.map((s) => s.id)} onToggle={toggle} showDriver={i === 0} />
                  ))}
                </div>
                <p className="muted small">Fares shown on each seat include taxes. Front of the bus is on the left.</p>
                {notice && <p className="text-coral small" role="status">{notice}</p>}
              </>
            )}
          </section>

          {map && (
            <section className="panel bus-stops">
              <PointList title="Boarding point" name="boarding" points={map.boardingPoints} value={boarding} onChange={setBoarding} />
              {needsDrop && <PointList title="Dropping point" name="dropping" points={map.droppingPoints} value={dropping} onChange={setDropping} />}
            </section>
          )}

          {bus?.cancellationPolicy?.length > 0 && (
            <section className="panel">
              <h3>Cancellation policy</h3>
              <ul className="policy-list">
                {cancellationLines(bus.cancellationPolicy).map((l) => <li key={l}>{l}</li>)}
                <li>{bus.partialCancellationAllowed ? 'You can cancel some seats and keep others.' : 'All seats on the ticket are cancelled together.'}</li>
                <li>The convenience fee is not refunded.</li>
              </ul>
            </section>
          )}
        </div>

        <aside className="details-side">
          <div className="panel price-card">
            <span className="muted small">{selected.length ? `${seatsLabel(selected.length)}: ${selected.map((s) => s.id).join(', ')}` : 'No seats selected'}</span>
            <b className="price big">{formatPrice(fare)}</b>
            {selected.length > 0 && (
              <div className="fare-breakdown">
                <div className="summary-line"><span>Base fare</span><span>{formatPrice(selected.reduce((sum, s) => sum + s.fare, 0))}</span></div>
                <div className="summary-line"><span>Taxes</span><span>{formatPrice(selected.reduce((sum, s) => sum + s.taxes, 0))}</span></div>
              </div>
            )}
            {map && selected.length > 0 && !boarding && <p className="muted small">Choose a boarding point.</p>}
            {map && selected.length > 0 && boarding && needsDrop && !dropping && <p className="muted small">Choose a dropping point.</p>}
            {reviewError && <div className="api-message error" role="alert">{reviewError}</div>}
            <button type="button" className="btn btn-primary block lg" onClick={continueToBook} disabled={!ready || busy}>
              <Icon name="bus" size={18} /> {busy ? 'Checking seats…' : 'Continue'}
            </button>
            <p className="muted small">Up to {maxSeats} seats per booking. The convenience fee, if any, is added at checkout.</p>
          </div>
        </aside>
      </div>
    </>
  );
}
